import { useMutation } from '@tanstack/react-query'

import { Logger } from '@core/logger'
import {
    revalidateAuthorCollections,
    getStoredAudios,
    removeAudioFromAudios,
    removeAudioFromAuthorPages,
    removeAudioQueries,
    resetAllAuthorAudios,
    restoreAuthorPages,
    updateAudios,
    deleteAudio,
} from '@features/audio'

type TDeleteAudioParams = {
    audioId: string
    audioSlug: string
    authorUsername: string
}

export const useDeleteAudio = () => {
    return useMutation({
        mutationFn: async ({ audioId, audioSlug, authorUsername }: TDeleteAudioParams) => {
            try {
                await deleteAudio(audioId)
            } catch (error) {
                Logger.error('Delete audio error:', {
                    error,
                    payload: { audioId, audioSlug, authorUsername },
                })

                throw new Error('Failed to delete audio')
            }
        },
        onMutate({ audioSlug, authorUsername }) {
            const cachedAudios = getStoredAudios(authorUsername)

            removeAudioFromAudios({ username: authorUsername, audioSlug })
            const pages = removeAudioFromAuthorPages({ username: authorUsername, audioSlug })

            return { audios: cachedAudios, pages }
        },
        onSuccess(_data, { audioId, authorUsername }) {
            removeAudioQueries(audioId)

            return Promise.all([
                resetAllAuthorAudios({ username: authorUsername }),
                revalidateAuthorCollections(),
            ])
        },
        onError(_error, { authorUsername }, context) {
            if (context?.audios) {
                updateAudios(authorUsername, context.audios)
            }

            if (context?.pages) {
                restoreAuthorPages(context.pages)
            }
        },
    })
}
